import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:9040',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Log para depuração (remover em produção se necessário)
      if (process.env.NODE_ENV === 'development') {
        console.log(`📤 Enviando requisição para: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`🔑 Token presente: ${token.substring(0, 20)}...`);
      }
    } else {
      console.warn('⚠️ Token não encontrado no localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token inválido ou expirado
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Só redirecionar se não estiver já na página de login
      if (window.location.pathname !== '/login') {
        // Usar replace para evitar voltar para a página anterior
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Serviços de autenticação
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  verifyOTP: async (tempToken, otpCode) => {
    const response = await api.post('/api/auth/verify-otp', { tempToken, otpCode });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  setupOTP: async (tempToken) => {
    const response = await api.post('/api/auth/setup-otp', { tempToken });
    return response.data;
  },

  enableOtp: async (tempToken, otpCode) => {
    const response = await api.post('/api/auth/enable-otp', { tempToken, otpCode });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
  
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  
  getMe: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  }
};

// Serviços de notificações
export const notificacoesService = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/notificacoes?${params}`);
    return response.data;
  },

  markAsRead: async (id) => {
    const response = await api.put(`/api/notificacoes/${id}/lida`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put('/api/notificacoes/lidas');
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/notificacoes/${id}`);
    return response.data;
  }
};

// Serviços de vendas
export const salesService = {
  getAll: async (filters = {}, page = 1, limit = 20) => {
    const paramsObj = { ...filters, page, limit };
    const params = new URLSearchParams(paramsObj).toString();
    const response = await api.get(`/api/sales?${params}`);
    return response.data;
  },
  
  getAllForStats: async (filters = {}) => {
    // Retrocompat: redireciona para contagens agregadas no servidor
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const response = await api.get(`/api/sales/stats/counts${params ? `?${params}` : ''}`);
    return response.data;
  },

  getStatsCounts: async (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const response = await api.get(`/api/sales/stats/counts${params ? `?${params}` : ''}`);
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/sales/${id}`);
    return response.data;
  },
  
  create: async (saleData, isFormData = false) => {
    const config = isFormData 
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    const response = await api.post('/api/sales', saleData, config);
    return response.data;
  },
  
  update: async (id, saleData) => {
    const response = await api.put(`/api/sales/${id}`, saleData);
    return response.data;
  },
  
  updateStatus: async (id, status, observacao_tratamento, data_instalacao, numero_ordem, data_conclusao, simulacao, link_aceite, imagem_anexo, motivo_imagem, numero_pedido_pap, extra = {}) => {
    const body = { status, observacao_tratamento };
    if (data_instalacao) body.data_instalacao = data_instalacao;
    if (numero_ordem) body.numero_ordem = numero_ordem;
    if (data_conclusao) body.data_conclusao = data_conclusao;
    if (simulacao) body.simulacao = simulacao;
    if (link_aceite) body.link_aceite = link_aceite;
    if (imagem_anexo) body.imagem_anexo = imagem_anexo;
    if (motivo_imagem) body.motivo_imagem = motivo_imagem;
    if (numero_pedido_pap) body.numero_pedido_pap = numero_pedido_pap;
    if (extra && extra.preservarAtribuicaoBko === true) {
      body.preservar_atribuicao_bko = true;
    }
    if (extra && extra.preservarDataInstalacaoSemDefault === true) {
      body.preservar_data_instalacao_sem_default = true;
    }
    const response = await api.put(`/api/sales/${id}/status`, body);
    return response.data;
  },

  uploadImagem: async (id, formData) => {
    const response = await api.put(`/api/sales/${id}/upload-imagem`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  verificarAceite: async (id) => {
    const response = await api.put(`/api/sales/${id}/verificar-aceite`);
    return response.data;
  },

  alterarEmailAceite: async (id, email) => {
    const response = await api.put(`/api/sales/${id}/alterar-email-aceite`, { email });
    return response.data;
  },

  alterarCelularAceite: async (id, telefone) => {
    const response = await api.put(`/api/sales/${id}/alterar-celular-aceite`, { telefone });
    return response.data;
  },

  verificarBiometria: async (id) => {
    const response = await api.put(`/api/sales/${id}/verificar-biometria`);
    return response.data;
  },

  clienteRespondeuWhatsapp: async (id) => {
    const response = await api.put(`/api/sales/${id}/cliente-respondeu-whatsapp`);
    return response.data;
  },

  clienteAtualizouSfa: async (id) => {
    const response = await api.put(`/api/sales/${id}/cliente-atualizou-sfa`);
    return response.data;
  },

  vendedorCancelouOuReagendou: async (id) => {
    const response = await api.put(`/api/sales/${id}/vendedor-cancelou-ou-reagendou`);
    return response.data;
  },

  addObservacaoSupervisao: async (id, texto) => {
    const response = await api.put(`/api/sales/${id}/observacoes-supervisao`, { texto });
    return response.data;
  },
  
  updateAtribuicaoBko: async (id, tratado_por) => {
    const response = await api.put(`/api/sales/${id}/atribuicao-bko`, { 
      tratado_por 
    });
    return response.data;
  },
  
  updateAtribuicaoVendedor: async (id, vendedor_id) => {
    const response = await api.put(`/api/sales/${id}/atribuicao-vendedor`, { 
      vendedor_id 
    });
    return response.data;
  },
  
  getQueue: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/sales/queue/list?${params}`);
    return response.data;
  },

  getFilaVisao: async () => {
    const response = await api.get('/api/sales/queue/visao');
    return response.data;
  },

  setFilaVerTudo: async (ativo) => {
    const response = await api.put('/api/sales/queue/ver-tudo', { ativo: !!ativo });
    return response.data;
  },
  
  getStats: async (mes, ano, canal) => {
    const params = new URLSearchParams();
    if (mes != null && mes >= 1 && mes <= 12) params.set('mes', String(mes));
    if (ano != null && ano >= 2000) params.set('ano', String(ano));
    if (['extk', 'onda', 'vision'].includes(canal)) params.set('canal', canal);
    const query = params.toString();
    const response = await api.get(`/api/sales/stats/dashboard${query ? `?${query}` : ''}`);
    return response.data;
  },
  
  buscarCnpj: async (cnpj) => {
    const response = await api.get(`/api/sales/utils/buscar-cnpj/${cnpj}`);
    return response.data;
  },
  
  // Vendas concluídas para controle de pagamento
  getVendasConcluidas: async (status_pagamento) => {
    const params = status_pagamento ? `?status_pagamento=${status_pagamento}` : '';
    const response = await api.get(`/api/sales/pagamento/concluidas${params}`);
    const payload = response.data;
    return Array.isArray(payload) ? payload : (payload?.data || []);
  },
  
  // Vendas concluídas com suporte a busca/paginação/período
  getVendasConcluidasComBusca: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        if (value.length) queryParams.set(key, value.join(','));
      } else {
        queryParams.set(key, String(value));
      }
    });
    const qs = queryParams.toString();
    const url = `/api/sales/pagamento/concluidas${qs ? `?${qs}` : ''}`;
    const response = await api.get(url);
    const payload = response.data;
    // Retrocompat: array puro OU { data, pagination, totais }
    if (Array.isArray(payload)) {
      return { data: payload, pagination: { page: 1, limit: payload.length, total: payload.length, totalPages: 1 }, totais: null };
    }
    return {
      data: payload?.data || [],
      pagination: payload?.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 },
      totais: payload?.totais || null
    };
  },
  
  updateStatusPagamento: async (id, status_pagamento, data_pagamento, data_pagamento_parcial, valor_parcial_pago, valor_total) => {
    const body = { status_pagamento };
    if (data_pagamento) body.data_pagamento = data_pagamento;
    if (data_pagamento_parcial) body.data_pagamento_parcial = data_pagamento_parcial;
    if (valor_parcial_pago != null) body.valor_parcial_pago = valor_parcial_pago;
    if (valor_total != null) body.valor_total = valor_total;
    const response = await api.put(`/api/sales/${id}/pagamento`, body);
    return response.data;
  },

  updateStatusPagamentoSupervisor: async (id, status_pagamento_supervisor, data_pagamento_supervisor, valor_pago_supervisor) => {
    const body = { status_pagamento_supervisor };
    if (data_pagamento_supervisor) body.data_pagamento_supervisor = data_pagamento_supervisor;
    if (valor_pago_supervisor != null) body.valor_pago_supervisor = valor_pago_supervisor;
    const response = await api.put(`/api/sales/${id}/pagamento-supervisor`, body);
    return response.data;
  },

  getResumoPagamentoPorOperadora: async (operadoraId, mes, ano, modo = 'a_receber') => {
    let url = `/api/sales/pagamento/resumo-por-operadora?operadora_id=${operadoraId}`;
    if (mes != null && ano != null) {
      url += `&mes=${mes}&ano=${ano}`;
    }
    if (modo === 'pagos') {
      url += '&modo=pagos';
    }
    const response = await api.get(url);
    return response.data;
  },

  getResumoPagamentoOnda: async (mes, ano, modo = 'a_receber') => {
    let url = `/api/sales/pagamento/resumo-marca/onda?`;
    const params = [];
    if (mes != null && ano != null) {
      params.push(`mes=${mes}`, `ano=${ano}`);
    }
    if (modo === 'pagos') {
      params.push('modo=pagos');
    }
    url += params.join('&');
    const response = await api.get(url);
    return response.data;
  },

  getResumoPagamentoMarca: async (marca, mes, ano, modo = 'a_receber') => {
    let url = `/api/sales/pagamento/resumo-marca/${marca}?`;
    const params = [];
    if (mes != null && ano != null) {
      params.push(`mes=${mes}`, `ano=${ano}`);
    }
    if (modo === 'pagos') {
      params.push('modo=pagos');
    }
    url += params.join('&');
    const response = await api.get(url);
    return response.data;
  },

  previewImportPagamento: async (file, modo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('modo', modo);
    const response = await api.post('/api/sales/pagamento/import-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  applyImportPagamento: async (file, modo, dataRef, resolucoes) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('modo', modo);
    if (dataRef) formData.append('data_ref', dataRef);
    if (resolucoes && Object.keys(resolucoes).length > 0) {
      formData.append('resolucoes', JSON.stringify(resolucoes));
    }
    const response = await api.post('/api/sales/pagamento/import-apply', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  processarVendas: async (file, tipoCliente, acao) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo_cliente', tipoCliente);
    formData.append('acao', acao);
    const response = await api.post('/api/sales/processar-vendas', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  processarPlanilha: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/sales/processar-planilha', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/sales/${id}`);
    return response.data;
  },
  
  exportSales: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await api.get(`/api/sales/export?${params}`, {
      responseType: 'blob'
    });
    
    // Criar link de download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'vendas.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// Serviços de usuários
export const usersService = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const response = await api.get(`/api/users${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/users/${id}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/users/${id}`);
    return response.data;
  },
  
  getVendedoresBySupervisor: async (supervisorId) => {
    const response = await api.get(`/api/users/supervisor/${supervisorId}/vendedores`);
    return response.data;
  },
  
  getOperadoras: async (userId) => {
    const response = await api.get(`/api/users/${userId}/operadoras`);
    return response.data;
  },
  
  updateOperadoras: async (userId, operadoras, atender_todas) => {
    const response = await api.put(`/api/users/${userId}/operadoras`, {
      operadoras,
      atender_todas
    });
    return response.data;
  },

  resetOtp: async (userId) => {
    const response = await api.post(`/api/users/${userId}/reset-otp`);
    return response.data;
  },

  getOtpStatus: async (userId) => {
    const response = await api.get(`/api/users/${userId}/otp-status`);
    return response.data;
  }
};

// Serviço de busca de CEP
export const viaCepService = {
  buscarCep: async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) {
      throw new Error('CEP inválido');
    }
    
    const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    
    if (response.data.erro) {
      throw new Error('CEP não encontrado');
    }
    
    return response.data;
  }
};

// Serviço de consulta de viabilidade
export const viabilidadeService = {
  consultar: async (cep, numero) => {
    const response = await api.post('/api/viabilidade/consultar', {
      cep,
      numero
    }, {
      timeout: 120000 // 2 minutos de timeout
    });
    return response.data;
  }
};

// Serviço de configuração da API
export const apiConfigService = {
  getCredentials: async () => {
    const response = await api.get('/api/api-config/credentials');
    return response.data;
  },
  
  createCredential: async (username, password) => {
    const response = await api.post('/api/api-config/credentials', {
      username,
      password
    });
    return response.data;
  },
  
  updateCredential: async (id, data) => {
    const response = await api.put(`/api/api-config/credentials/${id}`, data);
    return response.data;
  },
  
  deleteCredential: async (id) => {
    const response = await api.delete(`/api/api-config/credentials/${id}`);
    return response.data;
  },
  
  testConnection: async (username, password) => {
    const response = await api.post('/api/api-config/test', {
      username,
      password
    });
    return response.data;
  },
  
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    
    const response = await api.post('/api/api-config/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    if (response.data?.version) {
      try { localStorage.setItem('extk_logo_version', String(response.data.version)); } catch (_) { /* ignore */ }
    }
    return response.data;
  },
  
  getLogoUrl: (withVersion = true) => {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:9040';
    const base = `${baseURL}/api/api-config/logo`;
    if (!withVersion) return base;
    let version = null;
    try { version = localStorage.getItem('extk_logo_version'); } catch (_) { /* ignore */ }
    return version ? `${base}?v=${version}` : base;
  },

  refreshLogoVersion: async () => {
    try {
      const response = await api.get('/api/api-config/logo-version');
      const version = response.data?.version;
      if (version) {
        try { localStorage.setItem('extk_logo_version', String(version)); } catch (_) { /* ignore */ }
      }
      return version;
    } catch (_) {
      return null;
    }
  },

  // Token API DataNexus (consulta CPF)
  getCpfToken: async () => {
    const response = await api.get('/api/api-config/cpf-token');
    return response.data;
  },

  updateCpfToken: async (token) => {
    const response = await api.put('/api/api-config/cpf-token', { token });
    return response.data;
  },

  buscarCpf: async (cpf) => {
    const cpfLimpo = String(cpf).replace(/\D/g, '');
    const response = await api.get(`/api/api-config/buscar-cpf/${cpfLimpo}`);
    return response.data;
  },

  consultarEmpresasCpf: async (dados) => {
    const response = await api.post('/api/api-config/consultar-empresas-cpf', dados);
    return response.data;
  }
};

// Serviço de operadoras
export const operadorasService = {
  getAll: async () => {
    const response = await api.get('/api/operadoras');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/api/operadoras/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/operadoras/${id}`, data);
    return response.data;
  }
};

// Serviço de planos (por operadora)
export const planosService = {
  getByOperadora: async (operadoraId) => {
    const response = await api.get(`/api/planos?operadora_id=${operadoraId}`);
    return response.data;
  },
  
  create: async (data) => {
    const response = await api.post('/api/planos', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/api/planos/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/api/planos/${id}`);
    return response.data;
  }
};

// Serviço de anúncios
const getBaseUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:9040';

export const announcementsService = {
  create: async (data) => {
    const formData = new FormData();
    formData.append('titulo', data.titulo);
    formData.append('conteudo', data.conteudo);
    formData.append('data_inicio', data.data_inicio);
    formData.append('data_fim', data.data_fim);
    data.target_roles.forEach(role => formData.append('target_roles[]', role));
    if (data.imagem && data.imagem instanceof File) {
      formData.append('imagem', data.imagem);
    }
    const response = await api.post('/api/announcements', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getVisible: async () => {
    const response = await api.get('/api/announcements');
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/announcements?manage=1');
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/api/announcements/${id}`);
  },

  getImageUrl: (imagemPath) => {
    if (!imagemPath) return null;
    const base = getBaseUrl().replace(/\/$/, '');
    const path = imagemPath.startsWith('/') ? imagemPath : `/${imagemPath}`;
    return `${base}${path}`;
  }
};

// Serviço de arquivos
export const arquivosService = {
  list: async (busca = '') => {
    const params = busca ? { busca } : {};
    const response = await api.get('/api/arquivos', { params });
    return response.data;
  },

  upload: async (file, userIds) => {
    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('user_ids', JSON.stringify(userIds));
    const response = await api.post('/api/arquivos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  download: async (id, nomeOriginal) => {
    const response = await api.get(`/api/arquivos/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nomeOriginal || 'arquivo');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  delete: async (id) => {
    await api.delete(`/api/arquivos/${id}`);
  }
};

// Serviço catálogo de planos
const appendCatalogoNeoFields = (formData, data) => {
  if (data.nome_neo != null) formData.append('nome_neo', data.nome_neo);
  if (data.valor_padrao != null && data.valor_padrao !== '') formData.append('valor_padrao', data.valor_padrao);
  if (data.filtro_busca != null) formData.append('filtro_busca', data.filtro_busca);
  if (data.categoria != null) formData.append('categoria', data.categoria);
  if (data.tipo_selecao != null) formData.append('tipo_selecao', data.tipo_selecao);
};

export const catalogoPlanosService = {
  list: async (manage = false) => {
    const params = manage ? { manage: '1' } : {};
    const response = await api.get('/api/catalogo-planos', { params });
    return response.data;
  },

  listPfNeo: async () => {
    const response = await api.get('/api/catalogo-planos', { params: { pf_neo: '1' } });
    return response.data;
  },

  create: async (data) => {
    const formData = new FormData();
    formData.append('titulo', data.titulo);
    if (data.descricao != null) formData.append('descricao', data.descricao);
    if (data.ordem != null) formData.append('ordem', data.ordem);
    if (data.ativo != null) formData.append('ativo', data.ativo);
    if (data.imagem && data.imagem instanceof File) formData.append('imagem', data.imagem);
    appendCatalogoNeoFields(formData, data);
    const response = await api.post('/api/catalogo-planos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  update: async (id, data) => {
    const formData = new FormData();
    if (data.titulo != null) formData.append('titulo', data.titulo);
    if (data.descricao != null) formData.append('descricao', data.descricao);
    if (data.ordem != null) formData.append('ordem', data.ordem);
    if (data.ativo != null) formData.append('ativo', data.ativo);
    if (data.imagem && data.imagem instanceof File) formData.append('imagem', data.imagem);
    appendCatalogoNeoFields(formData, data);
    const response = await api.put(`/api/catalogo-planos/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  delete: async (id) => {
    await api.delete(`/api/catalogo-planos/${id}`);
  },

  getImageUrl: (imagemPath) => {
    if (!imagemPath) return null;
    const base = getBaseUrl().replace(/\/$/, '');
    const path = imagemPath.startsWith('/') ? imagemPath : `/${imagemPath}`;
    return `${base}${path}`;
  }
};

// Serviço InfoSpy (timeouts altos: fila externa grande demora; evita falha HTTP e reentrada na fila)
const INFOSPY_DEFAULT_CONSULTAR_TIMEOUT_MS = 180000;
const INFOSPY_DEFAULT_STATUS_TIMEOUT_MS = 120000;

// Serviço InfoSpy
export const infospyService = {
  // Iniciar consulta de CPF
  consultar: async (cpf, requestConfig = {}) => {
    const { timeout = INFOSPY_DEFAULT_CONSULTAR_TIMEOUT_MS, ...axiosRest } = requestConfig;
    const response = await api.post('/api/infospy/consultar', { cpf }, { timeout, ...axiosRest });
    return response.data;
  },

  // Consultar status da consulta
  getStatus: async (cpf, requestConfig = {}) => {
    const { timeout = INFOSPY_DEFAULT_STATUS_TIMEOUT_MS, ...axiosRest } = requestConfig;
    const response = await api.get(`/api/infospy/status/${cpf}`, { timeout, ...axiosRest });
    return response.data;
  },

  // Buscar consulta ativa (pending/processing) do usuário
  getConsultaAtiva: async (requestConfig = {}) => {
    const { timeout = INFOSPY_DEFAULT_STATUS_TIMEOUT_MS, ...axiosRest } = requestConfig;
    const response = await api.get('/api/infospy/consulta-ativa', { timeout, ...axiosRest });
    return response.data;
  },

  // Obter histórico de consultas
  getHistorico: async (filters = {}, page = 1, limit = 20) => {
    const paramsObj = { ...filters, page, limit };
    const params = new URLSearchParams(paramsObj).toString();
    const response = await api.get(`/api/infospy/historico?${params}`);
    return response.data;
  },

  // Obter detalhes de uma consulta específica
  getConsulta: async (id) => {
    const response = await api.get(`/api/infospy/consulta/${id}`);
    return response.data;
  },

  // Deletar uma consulta (admin/diretor)
  deleteConsulta: async (id) => {
    const response = await api.delete(`/api/infospy/consulta/${id}`);
    return response.data;
  },

  // Obter URL da imagem
  getImageUrl: (consultaId, filename) => {
    const base = api.defaults.baseURL.replace(/\/$/, '');
    const token = localStorage.getItem('token');
    return `${base}/api/infospy/imagens/${consultaId}/${filename}?token=${token}`;
  }
};

// InfoSpyPj — SmartConsultas (CNPJ+ordem) + sócios (CNPJA/DataNexus)
export const infoSpyPjService = {
  getOnline: async () => {
    const response = await api.get('/api/infospypj/online', { timeout: 15000 });
    return response.data;
  },

  consultar: async (cnpj, ordem) => {
    const response = await api.post(
      '/api/infospypj/consultar',
      { cnpj, ordem },
      { timeout: 45000 }
    );
    return response.data;
  },

  getStatus: async (id) => {
    const response = await api.get(`/api/infospypj/status/${id}`, { timeout: 30000 });
    return response.data;
  },

  getConsultaAtiva: async () => {
    const response = await api.get('/api/infospypj/consulta-ativa', { timeout: 30000 });
    return response.data;
  },

  getHistorico: async (filters = {}, page = 1, limit = 20) => {
    const params = new URLSearchParams({ ...filters, page, limit }).toString();
    const response = await api.get(`/api/infospypj/historico?${params}`);
    return response.data;
  },

  getConsulta: async (id) => {
    const response = await api.get(`/api/infospypj/consulta/${id}`);
    return response.data;
  },

  deleteConsulta: async (id) => {
    const response = await api.delete(`/api/infospypj/consulta/${id}`);
    return response.data;
  },

  buscarPorNome: async (nome, taxIdMascara) => {
    const response = await api.post(
      '/api/infospypj/buscar-por-nome',
      { nome, taxIdMascara },
      { timeout: 45000 }
    );
    return response.data;
  },

  buscarCpf: async (cpf) => {
    const response = await api.get(`/api/infospypj/buscar-cpf/${cpf}`, { timeout: 30000 });
    return response.data;
  }
};

// Consulta Cadastral (CPF)
export const consultaCadastralService = {
  consultar: async (cpf, requestConfig = {}) => {
    const response = await api.post(
      '/api/consulta-cadastral/consultar',
      { cpf },
      { timeout: 60000, ...requestConfig }
    );
    return response.data;
  },

  consultarDatanexus: async (tipo, valor, requestConfig = {}) => {
    const response = await api.post(
      '/api/consulta-cadastral/datanexus',
      { tipo, valor },
      { timeout: 30000, ...requestConfig }
    );
    return response.data;
  },

  getHistorico: async (filters = {}, page = 1, limit = 20) => {
    const paramsObj = { ...filters, page, limit };
    const params = new URLSearchParams(paramsObj).toString();
    const response = await api.get(`/api/consulta-cadastral/historico?${params}`);
    return response.data;
  },

  getHistoricoById: async (id) => {
    const response = await api.get(`/api/consulta-cadastral/historico/${id}`);
    return response.data;
  }
};

// Servico WhatsApp
export const whatsappService = {
  getStatus: async () => {
    const response = await api.get('/api/whatsapp/status');
    return response.data;
  },
  getQRCode: async () => {
    const response = await api.get('/api/whatsapp/qr');
    return response.data;
  },
  initialize: async () => {
    const response = await api.post('/api/whatsapp/initialize');
    return response.data;
  },
  restart: async () => {
    const response = await api.post('/api/whatsapp/restart');
    return response.data;
  },
  getTemplates: async () => {
    const response = await api.get('/api/whatsapp/templates');
    return response.data;
  },
  updateTemplate: async (id, data) => {
    const response = await api.put(`/api/whatsapp/templates/${id}`, data);
    return response.data;
  },
  sendTest: async (phone, message) => {
    const response = await api.post('/api/whatsapp/send-test', { phone, message });
    return response.data;
  },
  /** Remove sessão LocalAuth no servidor e reinicia (novo QR). */
  clearSession: async () => {
    const response = await api.post('/api/whatsapp/clear-session');
    return response.data;
  }
};

export const automacaoService = {
  getEstadoAtual: async () => {
    const response = await api.get('/api/automacao/estado-atual');
    return response.data;
  },
  iniciarValidacaoOk: async (vendaId = null) => {
    const body = vendaId != null && vendaId !== '' ? { venda_id: vendaId } : {};
    const response = await api.post('/api/automacao/validacao-ok/iniciar', body);
    return response.data;
  },
  cancelarValidacaoOk: async () => {
    const response = await api.post('/api/automacao/validacao-ok/cancelar');
    return response.data;
  },
  responderConfirmacaoBko: async (trabalhando) => {
    const response = await api.post('/api/automacao/confirmacao-bko/responder', { trabalhando });
    return response.data;
  },
  listRuns: async (limit = 20) => {
    const response = await api.get('/api/automacao/runs', { params: { limit } });
    return response.data;
  },
  listLogs: async (runId, params = {}) => {
    const response = await api.get(`/api/automacao/runs/${runId}/logs`, { params });
    return response.data;
  }
};

// DID (números virtuais)
export const comoFuncionaService = {
  getDashboard: async () => {
    const response = await api.get('/api/como-funciona');
    return response.data;
  },
  listNumeros: async (filters = {}) => {
    const response = await api.get('/api/como-funciona/numeros', { params: filters });
    return response.data;
  },
  listHistorico: async (filters = {}) => {
    const response = await api.get('/api/como-funciona/historico', { params: filters });
    return response.data;
  },
  updateConfig: async (data) => {
    const response = await api.put('/api/como-funciona/config', data);
    return response.data;
  },
  criarOrdem: async (quantidade) => {
    const response = await api.post('/api/como-funciona/ordens', { quantidade });
    return response.data;
  },
  listOrdens: async (filters = {}) => {
    const response = await api.get('/api/como-funciona/ordens', { params: filters });
    return response.data;
  },
  enviarComprovante: async (ordemId, file) => {
    const formData = new FormData();
    formData.append('comprovante', file);
    const response = await api.post(
      `/api/como-funciona/ordens/${ordemId}/comprovante`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
  confirmarPago: async (ordemId) => {
    const response = await api.put(`/api/como-funciona/ordens/${ordemId}/pago`);
    return response.data;
  },
  cancelarOrdem: async (ordemId) => {
    const response = await api.put(`/api/como-funciona/ordens/${ordemId}/cancelar`);
    return response.data;
  },
  cadastrarNumeros: async (ordemId, texto, destinoSupervisorId = null) => {
    const body = { texto };
    if (destinoSupervisorId) body.destino_supervisor_id = destinoSupervisorId;
    const response = await api.post(`/api/como-funciona/ordens/${ordemId}/numeros`, body);
    return response.data;
  },
  liberarManual: async ({ destino_id, texto, modo }) => {
    const response = await api.post('/api/como-funciona/numeros/manual', {
      destino_id,
      texto,
      modo
    });
    return response.data;
  },
  distribuirNumeros: async ({ vendedor_id, quantidade, supervisor_id }) => {
    const body = { vendedor_id, quantidade };
    if (supervisor_id) body.supervisor_id = supervisor_id;
    const response = await api.post('/api/como-funciona/numeros/distribuir', body);
    return response.data;
  },
  transferirNumeros: async ({ numero_ids, para_vendedor_id }) => {
    const response = await api.post('/api/como-funciona/numeros/transferir', {
      numero_ids,
      para_vendedor_id
    });
    return response.data;
  },
  usarLote: async ({ quantidade, supervisor_id }) => {
    const body = { quantidade };
    if (supervisor_id) body.supervisor_id = supervisor_id;
    const response = await api.post('/api/como-funciona/numeros/usar-lote', body);
    return response.data;
  },
  enviarNumero: async (numeroId, vendedorId) => {
    const response = await api.post(`/api/como-funciona/numeros/${numeroId}/enviar`, {
      vendedor_id: vendedorId
    });
    return response.data;
  },
  usarNumero: async (numeroId) => {
    const response = await api.post(`/api/como-funciona/numeros/${numeroId}/usar`);
    return response.data;
  },
  solicitarSenha: async (numeroId) => {
    const response = await api.post(`/api/como-funciona/numeros/${numeroId}/solicitar-senha`);
    return response.data;
  },
  atualizarSenha: async (numeroId, data) => {
    const response = await api.put(`/api/como-funciona/numeros/${numeroId}/senha`, data);
    return response.data;
  },
  getComprovanteUrl: (path) => {
    if (!path) return null;
    const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
    return path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  }
};

export default api;
